/**
 * An alert campaign's opening (ring R9): the one part the owner may change, and
 * what every defect makes of it.
 *
 * An alert call names the defect a recheck saw right before dialling, so the
 * sentence about it is written by server code per finding, never typed. What
 * stays editable is the question at the end; a blank one speaks the alert
 * default. The list under the field is the server's own build for a sample
 * company, one opening per defect, with its length and anything the call gate
 * would refuse, so the owner reads the question inside every sentence it can
 * land in. The offer's pitch is not shown: an alert call offers only the free
 * defect report.
 *
 * The preview is asked a moment after typing stops, and only the newest answer
 * may paint.
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Input } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";
import {
  alertClassKey,
  alertClassTone,
  alertFindingKey,
  alertVariantLong,
  previewAlertOpening,
  type AlertOpeningPreview,
} from "../../../../../lib/aiAlert";
import { OUTBOUND_TEXT_LIMITS } from "../../../../../lib/aiOutbound";
import { openingProblemKey } from "../../../../../lib/aiOutboundOpening";
import { StatusPill } from "../../../../ui";
import { Field, FieldGroup, Note, ToggleRow } from "../../../settings/panel/fields";

/** How long typing must pause before the preview is asked for. */
const PREVIEW_DELAY_MS = 400;

export function AlertOpeningCard({
  question,
  onChange,
  disabled,
}: {
  question: string;
  onChange: (question: string) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const [preview, setPreview] = useState<AlertOpeningPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [mobile, setMobile] = useState(false);
  const request = useRef(0);
  const sent = question.trim();

  useEffect(() => {
    // Read-only (no manage right): the route would refuse, and a failure note would read as a fault.
    if (disabled) return;
    const id = ++request.current;
    setLoading(true);
    const timer = setTimeout(() => {
      previewAlertOpening(sent)
        .then((next) => {
          if (id !== request.current) return;
          setPreview(next);
          setFailed(false);
        })
        .catch(() => {
          if (id === request.current) setFailed(true);
        })
        .finally(() => {
          if (id === request.current) setLoading(false);
        });
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [sent, disabled]);

  const problemText = (codes: string[]) =>
    codes.map((code) => t(`aiOutbound.opening.problem.${openingProblemKey(code)}`)).join(", ");

  return (
    <section className="crm-card px-[26px] pb-6 pt-5">
      <FieldGroup title={t("aiCalls.campaigns.alert.title")}>
        <Note tone="info">{t("aiCalls.campaigns.alert.fixedNote")}</Note>

        <Field label={t("aiCalls.campaigns.alert.question")} hint={t("aiCalls.campaigns.alert.questionHint", { max: OUTBOUND_TEXT_LIMITS.question })}>
          <Input
            value={question}
            maxLength={OUTBOUND_TEXT_LIMITS.question}
            placeholder={preview?.defaultQuestion ?? ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
        {preview && preview.problems.length > 0 ? (
          <Note tone="bad">{t("aiCalls.campaigns.alert.problems", { problems: problemText(preview.problems) })}</Note>
        ) : null}

        {disabled ? null : (
          <div className="space-y-3 rounded-xl border border-border bg-card/60 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="crm-lab !mt-0">{t("aiCalls.campaigns.alert.preview")}</span>
              {loading ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" /> : null}
            </div>
            <ToggleRow
              label={t("aiCalls.campaigns.alert.mobile")}
              hint={t("aiCalls.campaigns.alert.mobileHint")}
              checked={mobile}
              onChange={setMobile}
            />
            {failed && !preview ? <p className="crm-sub text-xs">{t("aiCalls.campaigns.alert.previewFailed")}</p> : null}
            {preview ? (
              <ul className="divide-y divide-border">
                {preview.variants.map((variant) => {
                  const findingKey = alertFindingKey(variant.findingId);
                  const classKey = alertClassKey(variant.class);
                  return (
                    <li key={variant.findingId} className="space-y-1 py-2.5 first:pt-0 last:pb-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-[13px] font-[600]">{findingKey ? t(findingKey) : variant.findingId}</span>
                        <StatusPill tone={alertClassTone(variant.class)}>{classKey ? t(classKey) : variant.class}</StatusPill>
                        <span className="crm-sub ml-auto text-xs tabular-nums">
                          {t("aiCalls.campaigns.alert.seconds", { seconds: variant.seconds, mobileSeconds: variant.mobileSeconds })}
                        </span>
                      </div>
                      <p className="text-[13px] leading-relaxed">{mobile ? variant.mobileText : variant.text}</p>
                      {alertVariantLong(variant, preview.longSeconds) ? (
                        <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                          <AlertTriangle className="size-3.5" />
                          {t("aiCalls.campaigns.alert.long", { seconds: preview.longSeconds })}
                        </p>
                      ) : null}
                      {variant.problems.length > 0 ? (
                        <p className="text-xs text-destructive">
                          {t("aiCalls.campaigns.alert.variantProblems", { problems: problemText(variant.problems) })}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        )}
      </FieldGroup>
    </section>
  );
}
